import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { AppointmentsList as AppointmentsListComponent } from './components/AppointmentsList';
import { AppointmentsCalendar } from './components/AppointmentsCalendar';
import { ViewTabs } from './components/ViewTabs';

type ViewMode = 'list' | 'calendar';

export const AppointmentsList = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchParams, setSearchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const searchQuery = searchParams.get('search');
  const [autoEditAppointmentId, setAutoEditAppointmentId] = useState<
    string | null
  >(null);

  // Load view mode from localStorage on component mount
  useEffect(() => {
    const savedViewMode = localStorage.getItem(
      'appointment-view-mode'
    ) as ViewMode;
    if (
      savedViewMode &&
      (savedViewMode === 'list' || savedViewMode === 'calendar')
    ) {
      setViewMode(savedViewMode);
    }
  }, []);

  // Effect to handle auto-edit appointment
  useEffect(() => {
    const storedAppointmentId = localStorage.getItem('autoEditAppointmentId');
    const storedTimestamp = localStorage.getItem('autoEditTimestamp');

    if (storedAppointmentId && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp);
      const currentTime = Date.now();

      // Only process if the timestamp is recent (within 10 seconds)
      if (currentTime - timestamp < 10000) {
        // Clear it from localStorage to prevent it from persisting
        localStorage.removeItem('autoEditAppointmentId');
        localStorage.removeItem('autoEditTimestamp');

        // Set the auto edit appointment ID
        setAutoEditAppointmentId(storedAppointmentId);

        // Clear the auto edit ID after a longer delay to ensure the form opens properly
        const timer = setTimeout(() => {
          setAutoEditAppointmentId(null);
        }, 3000);

        return () => clearTimeout(timer);
      } else {
        // Clear old entries
        localStorage.removeItem('autoEditAppointmentId');
        localStorage.removeItem('autoEditTimestamp');
      }
    }
  }, []);

  // Save view mode to localStorage when it changes
  const handleViewChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
    localStorage.setItem('appointment-view-mode', newViewMode);
  };

  // Handle create drawer close - remove patientId from URL
  const handleCreateDrawerClose = () => {
    if (patientId) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('patientId');
      setSearchParams(newSearchParams);
    }
  };

  // Clear auto edit ID after it's been used
  const handleAutoEditComplete = () => {
    setAutoEditAppointmentId(null);
  };

  return (
    <div className="h-full flex flex-col">
      {viewMode === 'list' ? (
        <AppointmentsListComponent
          onViewChange={handleViewChange}
          viewTabs={
            <ViewTabs currentView={viewMode} onViewChange={handleViewChange} />
          }
          patientId={patientId}
          searchQuery={searchQuery}
          onCreateDrawerClose={handleCreateDrawerClose}
          autoEditAppointmentId={autoEditAppointmentId}
          onAutoEditComplete={handleAutoEditComplete}
        />
      ) : (
        <AppointmentsCalendar
          onViewChange={handleViewChange}
          viewTabs={
            <ViewTabs currentView={viewMode} onViewChange={handleViewChange} />
          }
          patientId={patientId}
          onCreateDrawerClose={handleCreateDrawerClose}
        />
      )}
    </div>
  );
};
